import React, { Fragment, useContext, useEffect } from "react";
import useSWR from "swr";
import { useFormik } from "formik";
import { object, string, array } from "yup";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import MaterialTable from "../../../components/materialTable";
import * as RolesRequest from "../../../services/requests/mi_cyd/roles";
import * as PerfilesRequest from "../../../services/requests/mi_cyd/perfiles";
import { MainContext } from "../../../App";

export default function TabRoles(props) {
	const RolesSWR = useSWR("roles", (key) => RolesRequest.Obtener(), { revalidateOnFocus: false });
	const PerfilesSWR = useSWR("perfiles", (key) => PerfilesRequest.Obtener(), { revalidateOnFocus: false });
	const { ShowSnackbar } = useContext(MainContext);

	useEffect(() => {
		if (RolesSWR.error) {
			ShowSnackbar("Error al intentar obtener los roles.", RolesSWR.error);
		}
		if (PerfilesSWR.error) {
			ShowSnackbar("Error al intentar obtener los perfiles.", PerfilesSWR.error);
		}
	}, [RolesSWR.error, PerfilesSWR.error]);

	const formik = useFormik({
		initialValues: {
			nombre: "",
			descripcion: "",
			_perfiles_ref: [],
		},
		validationSchema: object().shape({
			nombre: string()
				.min(3, "El nombre debe tener al menos ${min} caracteres.")
				.max(50, "El nombre debe tener a los más ${max} caracteres.")
				.required("El nombre es requerido."),
			descripcion: string()
				.min(3, "La descripción debe tener al menos ${min} caracteres.")
				.max(50, "La descripción debe tener a los más ${max} caracteres.")
				.required("La descripción es requerida."),
			_perfiles_ref: array()
				.of(string())
				.optional(),
		}),
		onSubmit: (values, helper) => handleAceptar(values),
		enableReinitialize: true,
	});

	/**
	 * Handler para agregar un nuevo rol.
	 */
	const handleAgregar = () => {
		formik.setFieldValue("tipo", "agregar");
	}

	/**
	 * Handler para actualizar un rol.
	 * @param {*} event Evento.
	 * @param {*} row Datos.
	 */
	const handleActualizar = (event, row) => {
		formik.setValues(row);
		formik.setFieldValue("tipo", "actualizar");
	}

	/**
	 * Handler para eliminar un rol.
	 * @param {*} event Evento.
	 * @param {*} row Datos.
	 */
	const handleEliminar = (event, row) => {
		formik.setValues(row);
		formik.setFieldValue("tipo", "eliminar");
	}

	/**
	 * Handler para aceptar la acción.
	 * @param {*} values 
	 */
	const handleAceptar = async (values) => {
		try {
			switch (values.tipo) {
				case "agregar":
					await RolesRequest.Agregar(values);
					break;
				case "actualizar":
					await RolesRequest.Actualizar(values);
					break;
				case "eliminar":
					await RolesRequest.Eliminar(values);
					break;
				default:
					throw new Error("Acción no especificada.")
			}
			ShowSnackbar("Acción realizada exitosamente.");
		} catch (error) {
			ShowSnackbar("Error al intentar realizar la acción.", error);
		} finally {
			handleCancelar();
		}
	}

	const handleCancelar = () => {
		RolesSWR.mutate();
		PerfilesSWR.mutate();
		formik.resetForm();
	}

	let columns = [
		{
			title: "Nombre",
			field: "nombre",
			defaultSort: 'asc'
		}, {
			title: "Descripción",
			field: "descripcion",
		}
	];

	let actions = [
		{
			tooltip: "Agregar",
			icon: "add",
			onClick: () => handleAgregar(),
			isFreeAction: true,
		}, {
			tooltip: "Editar",
			icon: "edit",
			onClick: handleActualizar,
		}, {
			tooltip: "Eliminar",
			icon: "delete",
			onClick: handleEliminar,
		}
	];

	return (
		<Fragment>
			<MaterialTable
				title="Roles"
				is_loading={RolesSWR.isValidating}
				data={RolesSWR && RolesSWR.data?.data}
				columns={columns}
				actions={actions}
			/>
			<Dialog open={Boolean(formik.values.tipo)} maxWidth="md" fullWidth>
				<DialogTitle>
					{formik.values.tipo === "agregar" && "Agregar Rol"}
					{formik.values.tipo === "actualizar" && "Actualizar Rol"}
					{formik.values.tipo === "eliminar" && "Eliminar Rol"}
				</DialogTitle>
				<DialogContent dividers>
					<DialogContentText>Formulario con información del Rol.</DialogContentText>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								label="Nombre"
								name="nombre"
								value={formik.values.nombre}
								onChange={formik.handleChange}
								variant="outlined"
								fullWidth
								disabled={formik.values.tipo === "eliminar"}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Descripción"
								name="descripcion"
								value={formik.values.descripcion}
								onChange={formik.handleChange}
								variant="outlined"
								fullWidth
								disabled={formik.values.tipo === "eliminar"}
							/>
						</Grid>
						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={PerfilesSWR ? PerfilesSWR.data?.data : []}
								value={PerfilesSWR && PerfilesSWR.data?.data ? Array.from(PerfilesSWR.data.data).filter(p => formik.values._perfiles_ref.includes(p._id)) : []}
								getOptionLabel={(perfil) => perfil.nombre}
								disabled={formik.values.tipo === "eliminar"}
								onChange={(event, value) => {
									formik.setFieldValue("_perfiles_ref", value.map(v => v._id));
								}}
								renderInput={(params) => (
									<TextField
										label="Perfiles"
										variant="outlined"
										{...params}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelar} variant="outlined" color="primary">
						Cancelar
					</Button>
					<Button onClick={formik.submitForm} variant="contained" color="primary">
						Aceptar
					</Button>
				</DialogActions>
			</Dialog>
		</Fragment>
	);
}